// 스테이지 지도 화면의 표시 데이터를 만드는 순수 로직(DOM·three 없음). 정본: docs/STAGE-WORLD-PRD.md 3.1절, 목표 원화 concept/stage-maps-20261023/ui-stage-select.webp·ui-boss-stage-card.webp.
// 수치는 shared/content/stageRewards.ts(반복 보상·첫 클리어·확정 드롭·스페셜 확률)가 만든다. 이 파일은 문구·노드 상태·카드 구성만 맡는다.
import { XP_BAND } from '../shared/growth';
import { REGION_IDS, type RegionId } from '../shared/levels';
import { REGIONS } from '../shared/regions';
import { equipDef } from '../shared/gear';
import { GRADES } from '../shared/content/items';
import { BOSS_DEFS, REGION_BOSSES } from '../shared/content/bosses';
import { hardStageIdOf, hellStageIdOf, stageKind, stageIdOf, stagePlan } from '../shared/content/stages';
import { bossDrops, firstClearGearPlan, firstClearReward, repeatReward, specialChanceFor, stoneTierName, type Range } from '../shared/content/stageRewards';
import { DIFFICULTY, STAGES_PER_REGION, TOWNS_90D, stageById } from '../shared/content/world';
import type { DifficultyKey } from '../shared/content/special';
import type { AnyStageId, StageId } from '../shared/content/types';
import { fmtNum } from './dom';
import { RAGE_TEXT, rageKindOf, rageThreshold } from '../game/bossRage';

export type NodeState = 'cleared' | 'current' | 'open' | 'locked';
export type NodeKind = 'normal' | 'mid' | 'final';

/** 진행 뷰(필요한 것만) */
export interface MapProgress {
  selected(region: RegionId): string | null;
  regionUnlocked(region: RegionId): boolean;
  unlocked(id: string): boolean;
  stageClears(id: string): number;
  stageStars(id: string): number;
  /** 첫 클리어 보상을 받았는지(없으면 깬 스테이지만 받은 것으로 본다) */
  claimedFirst?(id: string): boolean;
  readonly cleared: number;
}

/**
 * 스테이지 지도 이름: 실제로 입장할 지도(stagePlan의 map)에서 끌어온다. 지역1은 A·A′·M·B·B′·F 여섯 장(시간대 변형은 지도 이름에 '(해질녘)'·'(아침 안개)'로 들어 있다),
 * 지역2~4는 아직 지역 지도 한 장뿐이라 그 이름 하나다 — 구현되지 않은 지도·시간대 변형을 광고하지 않는다(PR #175 2차 리뷰)
 */
export function stageMapName(regionNo: number, index: number): string {
  if (regionNo < 1 || regionNo > REGION_IDS.length || index < 1 || index > STAGES_PER_REGION) return '';
  return stagePlan(stageIdOf(regionNo, index)).map.name;
}

/** 노드 위치(지도 영역 안 비율 0..1). 굽이진 S자 길(위 줄 1~5 → 오른쪽 모서리 → 아래 줄 6~10). 목표 원화의 굽이를 따르되 노드 사이를 44px 터치 대상·이름표·별이 겹치지 않게 벌렸다 */
export const NODE_POS: readonly (readonly [number, number])[] = [
  [0.08, 0.3], [0.26, 0.22], [0.44, 0.3], [0.62, 0.22], [0.8, 0.3],
  [0.93, 0.5], [0.8, 0.66], [0.62, 0.58], [0.44, 0.66], [0.26, 0.58],
];

export interface MapNode {
  readonly index: number;
  /** 노말 'r1-s04', 하드 'h1-s04' */
  readonly id: AnyStageId;
  readonly label: string;
  readonly state: NodeState;
  /** 최고 별 0..3, 별 기록이 없는 깬 스테이지는 -1(깼는지만 보인다) */
  readonly stars: number;
  readonly kind: NodeKind;
  readonly selected: boolean;
  readonly x: number;
  readonly y: number;
}

/** 스테이지 id(노말 r…, 하드 h…, 헬 x…) */
export function mapStageId(no: number, index: number, diff: DifficultyKey): AnyStageId {
  return diff === 'hell' ? hellStageIdOf(no, index) : diff === 'hard' ? hardStageIdOf(no, index) : stageIdOf(no, index);
}

/**
 * 지역의 10개 노드. frontier = 지금 도전할 스테이지(아직 안 깬 첫 스테이지).
 * diff가 hard면 progress는 하드 진행(view.hard)이고 노드 id는 하드 스테이지 id(h1-s04)다
 */
export function mapNodes(progress: MapProgress, region: RegionId, selectedIndex: number, diff: DifficultyKey = 'normal'): MapNode[] {
  const no = REGION_IDS.indexOf(region) + 1;
  const out: MapNode[] = [];
  let frontierSeen = false;
  for (let i = 1; i <= STAGES_PER_REGION; i++) {
    const id = mapStageId(no, i, diff);
    const unlocked = progress.unlocked(id);
    const clears = progress.stageClears(id);
    let state: NodeState;
    if (!unlocked) state = 'locked';
    else if (clears > 0) state = 'cleared';
    else if (!frontierSeen) { state = 'current'; frontierSeen = true; } else state = 'open';
    const stars = state === 'cleared' ? (progress.stageStars(id) > 0 ? progress.stageStars(id) : -1) : 0;
    out.push({ index: i, id, label: `${no}-${i}`, state, stars, kind: stageKind(i), selected: i === selectedIndex, x: NODE_POS[i - 1][0], y: NODE_POS[i - 1][1] });
  }
  return out;
}

// ── 난이도 탭 ───────────────────────────────────────
export interface DiffTab { readonly key: DifficultyKey; readonly label: string; readonly locked: boolean; readonly active: boolean; /** 잠긴 탭에 보일 해금 조건 안내 */ readonly hint?: string }
/** 헬 탭 해금 조건 안내: 노말 전부(6-10) + 하드 그 지역 n-10 + 헬 앞 지역 */
export function hellLockHint(region: RegionId): string {
  const no = REGION_IDS.indexOf(region) + 1;
  return `노말 ${REGION_IDS.length}-10 · 하드 ${no}-10 클리어 시` + (no > 1 ? ` · 헬 ${no - 1}-10 클리어 후` : '');
}
/**
 * 해금된 난이도만 눌린다. 노말은 항상, 하드는 hardOpen(문 앞 카드 난이도 버튼과 같은 소스: view.hard.available && regionUnlocked)일 때,
 * 헬은 hellOpen(view.hell.available && regionUnlocked)일 때. 잠겼으면 hellHint가 해금 조건 안내
 */
export function difficultyTabs(current: DifficultyKey = 'normal', hardOpen = false, hellOpen = false, hellHint = ''): DiffTab[] {
  return [
    { key: 'normal', label: '노말', locked: false, active: current === 'normal' },
    { key: 'hard', label: '하드', locked: !hardOpen, active: hardOpen && current === 'hard' },
    { key: 'hell', label: '헬', locked: !hellOpen, active: hellOpen && current === 'hell', ...(!hellOpen && hellHint ? { hint: hellHint } : {}) },
  ];
}

// ── 보상 줄 ─────────────────────────────────────────
export type IconKind = 'gold' | 'exp' | 'stone' | 'gear' | 'gem' | 'set' | 'legend' | 'chest' | 'town';
export interface RewardTile { readonly icon: IconKind; /** 타일 아래 숫자·짧은 글자 */ readonly text: string; /** 접근성·툴팁 */ readonly title: string }

function rangeText(r: Range): string {
  return r.min === r.max ? fmtNum(r.min) : fmtNum(r.min) + '~' + fmtNum(r.max);
}

export interface StageCard {
  /** 입장 명령에 쓰는 스테이지 id(노말 'r1-s04', 하드 'h1-s04', 헬 'x1-s04') */
  readonly id: AnyStageId;
  readonly regionNo: number;
  readonly index: number;
  readonly kind: NodeKind;
  /** '1-4' */
  readonly label: string;
  /** 큰 제목: 일반 '1-4 절벽 테라스 유적(해질녘)', 보스 '지역 보스 · 고목 수호자' */
  readonly title: string;
  readonly blurb: string;
  readonly recLevel: number;
  readonly tabs: readonly DiffTab[];
  readonly diff: DifficultyKey;
  /** 보스 경고 줄('강력한 보스 출현', 일반은 '') */
  readonly warning: string;
  /** S5 분노·광폭 줄(보스판만): 종류(1 분노·2 광폭, 0 없음)·이름·설명('체력 30% 이하 분노 — 공격 속도 +20%, 추가 패턴 자주') */
  readonly rage: { readonly kind: 0 | 1 | 2; readonly name: string; readonly text: string; readonly pct: number };
  /** 첫 클리어 보상 타일(받았거나 잠겼으면 빈 배열) */
  readonly first: readonly RewardTile[];
  /** 첫 클리어 줄 설명: '' | '받음' */
  readonly firstNote: string;
  /** 반복 보상 타일(골드·EXP·강화석) */
  readonly repeat: readonly RewardTile[];
  /** 확정 드롭 타일(보스, 일반은 빈 배열) */
  readonly drops: readonly RewardTile[];
  /** 스페셜 몬스터 출현 확률 문구('15%', 보스판은 '') */
  readonly special: string;
  /** 지역보스 첫 클리어로 열리는 이동('잿불 전초기지 이동 해금', 아니면 '') */
  readonly travel: string;
  readonly locked: boolean;
  /** 입장 버튼 글자 */
  readonly enterLabel: string;
  readonly cleared: boolean;
  readonly clears: number;
  readonly stars: number;
}

const KIND_LABEL: Record<NodeKind, string> = { normal: '일반', mid: '중간보스', final: '지역보스' };
export function kindLabel(k: NodeKind): string { return KIND_LABEL[k]; }

/**
 * 선택한 스테이지의 오른쪽 카드 내용. diff가 hard면 progress는 하드 진행(view.hard)이고, 입장 id는 하드 스테이지 id(h1-s04)다.
 * 보상 수치는 노말 스테이지 표에 난이도 배율(repeatReward 등의 diff)을 곱해 보인다. 하드 지역 이동은 없다(travel은 노말 n-10 첫 클리어만)
 */
export function stageCard(progress: MapProgress, region: RegionId, index: number, diff: DifficultyKey = 'normal', hardOpen = false, hellOpen = false): StageCard {
  const no = REGION_IDS.indexOf(region) + 1;
  const base = stageIdOf(no, index);
  const id = mapStageId(no, index, diff);
  const hard = diff === 'hard';
  const hell = diff === 'hell';
  const kind = stageKind(index);
  const def = stageById(base);
  const info = REGIONS[region];
  const locked = !progress.unlocked(id);
  const clears = progress.stageClears(id);
  // 첫 클리어 보상: 노말·하드 모두 스테이지별 1회(하드도 같은 표, 강화석 등급만 하드 아이템 레벨, #175 P1). 수령 여부는 그 난이도 진행으로
  const claimed = progress.claimedFirst ? progress.claimedFirst(id) : (clears > 0);
  const rep = repeatReward(base, diff);
  const first = firstClearReward(base, diff);
  const drops = bossDrops(base, diff);
  const bossKey = kind === 'mid' ? REGION_BOSSES[no - 1].mid : kind === 'final' ? REGION_BOSSES[no - 1].final : null;
  const boss = bossKey ? BOSS_DEFS[bossKey] : null;

  const firstTiles: RewardTile[] = [];
  if (!claimed) {
    firstTiles.push({ icon: 'gold', text: fmtNum(first.gold), title: '첫 클리어 골드' });
    firstTiles.push({ icon: 'stone', text: String(first.stones), title: stoneTierName(first.stoneTier) + ' ' + first.stones + '개' });
    for (const g of firstClearGearPlan(first)) {
      const gd = GRADES.find((x) => x.id === g.minGrade);
      firstTiles.push({ icon: g.minGrade === 'set' ? 'set' : 'gear', text: g.minGrade === 'set' ? '세트' : (gd ? gd.name + '+' : '장비'), title: g.label + '(' + (gd ? gd.name : '') + ' 이상, 전설 제외) 1개' });
    }
  }
  const repeatTiles: RewardTile[] = [
    { icon: 'exp', text: rangeText(rep.exp), title: '클리어 EXP(레벨 차·고레벨 보정 전 — 영웅 Lv' + XP_BAND.from + '부터 줄어 Lv' + XP_BAND.full + '+ ×' + XP_BAND.mult + ')' },
    { icon: 'gold', text: rangeText(rep.gold), title: '클리어 골드 + 처치 골드 + 카드 1장' + (rep.mult !== 1 ? ' (클리어·카드 ×' + rep.mult + ')' : '') },
    { icon: 'stone', text: rangeText(rep.stones), title: stoneTierName(rep.stoneTier) + ' 기대 ' + rep.stonesExpected + '개' },
  ];
  const dropTiles: RewardTile[] = [];
  if (drops.equips > 0) {
    dropTiles.push({ icon: 'gear', text: '장비 ' + drops.equips, title: '보스 확정 장비 ' + drops.equips + '개(영웅 이상 ' + Math.round(drops.epicPlus * 100) + '%)' });
    if (drops.legend > 0) dropTiles.push({ icon: 'legend', text: '전설 ' + Math.round(drops.legend * 100) + '%', title: '전설 장비 확률' });
    if (drops.gems > 0) dropTiles.push({ icon: 'gem', text: '보석 ' + drops.gems, title: '보석 확정' });
  }
  const sp = specialChanceFor(base, diff);
  const nextTown = TOWNS_90D[no]?.name;
  const travel = !hard && !hell && first.travel && nextTown && !claimed ? nextTown + ' 이동 해금' : '';
  const names = stageMapName(no, index);
  const title = boss ? (kind === 'final' ? '지역 보스 · ' : '중간 보스 · ') + boss.name : `${hell ? '헬 ' : hard ? '하드 ' : ''}${no}-${index} ${names}`;
  const blurb = boss ? boss.blurb : index <= 5 ? info.blurb : info.roomTips[0] || info.blurb;
  return {
    id, regionNo: no, index, kind, label: `${no}-${index}`, title, blurb,
    recLevel: def ? def.recLevel + DIFFICULTY[diff].mobLevelAdd : 0, tabs: difficultyTabs(diff, hardOpen, hellOpen, hellOpen ? '' : hellLockHint(region)), diff,
    warning: boss ? (kind === 'final' ? '강력한 보스 출현' : '중간보스 출현') : '',
    rage: rageLine(boss),
    first: firstTiles, firstNote: claimed ? '받음' : '', repeat: repeatTiles, drops: dropTiles,
    special: sp > 0 ? Math.round(sp * 100) + '%' : '', travel, locked,
    enterLabel: locked ? '잠김' : kind === 'normal' ? '입장' : '보스 도전',
    cleared: clears > 0, clears, stars: progress.stageStars(id),
  };
}

/** 보스 정의 → 입장 카드 분노·광폭 줄(스테이지 판 보스는 노말·하드 모두 같은 규칙, game/bossRage) */
function rageLine(boss: (typeof BOSS_DEFS)[keyof typeof BOSS_DEFS] | null): StageCard['rage'] {
  const k = boss ? rageKindOf(boss, true) : 0;
  if (!boss || (k !== 1 && k !== 2)) return { kind: 0, name: '', text: '', pct: 0 };
  return { kind: k, name: RAGE_TEXT[k].name, text: RAGE_TEXT[k].card, pct: Math.round(rageThreshold(boss, true) * 100) };
}

/** 지도 머리말(지역 이름·소개) */
export function mapHeader(region: RegionId): { name: string; blurb: string } {
  const info = REGIONS[region];
  return { name: info.name, blurb: info.blurb };
}

// ── 결과 화면: 첫 클리어 받은 것 ────────────────────
export interface FirstClearView {
  readonly cleared: boolean;
  readonly firstStones: number;
  readonly firstStoneTier: number;
  readonly firstGear: readonly string[];
  readonly firstGold: number;
}
/** '첫 클리어 보상 · 강화석(하) 2 · [희귀] 훈련용 투구'. 받은 것이 없으면 '' (골드는 보상 내역 줄에 들어 있다) */
export function firstClearItemsText(r: FirstClearView): string {
  const parts: string[] = [];
  if (r.firstStones > 0) parts.push((['강화석(하)', '강화석(중)', '강화석(상)', '강화석(최상)'][r.firstStoneTier] ?? '강화석') + ' ' + r.firstStones);
  for (const g of r.firstGear) {
    const [grade, def] = g.split(':');
    const gd = GRADES.find((x) => x.id === grade);
    const d = equipDef(def);
    parts.push('[' + (gd ? gd.name : '') + '] ' + (d ? d.name : '장비'));
  }
  return parts.length ? '첫 클리어 보상 · ' + parts.join(' · ') : '';
}
